import { useState, useEffect, useCallback } from 'react';
import {
	Wrench,
	ChevronDown,
	Plus,
	Info,
	AlertTriangle,
	Globe,
	Trash2,
	BookOpen
} from 'lucide-react';
import { APIFunctionsDialog } from './APIFunctionsDialog';
import { APIFunctionsHelpDialog } from './APIFunctionsHelpDialog';

const { ipcRenderer } = window.require('electron');

const APIFunctionsSection = ({
	instanceId,
	showAPIFunctionsSettings,
	toggleAPIFunctionsSettings,
	settings,
	knowledgeBaseActive,
	onSettingsChange
}) => {
	const [functions, setFunctions] = useState([]);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isHelpOpen, setIsHelpOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const loadFunctions = useCallback(async () => {
		if (!instanceId) return;

		setIsLoading(true);
		try {
			const response = await ipcRenderer.invoke('list-functions', { instanceId });
			if (response.success) {
				setFunctions(response.functions || []);
			}
		} catch (error) {
			console.error('Erro ao carregar functions:', error);
		} finally {
			setIsLoading(false);
		}
	}, [instanceId]);

	useEffect(() => {
		if (showAPIFunctionsSettings && instanceId) {
			loadFunctions();
		}
	}, [showAPIFunctionsSettings, instanceId, loadFunctions]);

	useEffect(() => {
		if (!instanceId) return;
		const handler = () => loadFunctions();
		window.addEventListener("instance-data-imported", handler);
		return () => window.removeEventListener("instance-data-imported", handler);
	}, [instanceId, loadFunctions]);

	const handleOpenDialog = () => {
		setIsDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setIsDialogOpen(false);
		loadFunctions();
	};

	const handleDeleteFunction = async (functionId) => {
		try {
			const response = await ipcRenderer.invoke('delete-function', {
				instanceId,
				functionId
			});
			if (response.success) {
				loadFunctions();
			}
		} catch (error) {
			console.error('Erro ao deletar function:', error);
		}
	};

	const isEnabled = settings?.API_FUNCTIONS_ENABLED !== false;

	return (
		<>
			<div className="relative overflow-hidden bg-gradient-to-br from-amber-500/25 via-orange-500/20 to-yellow-500/25 rounded-xl border border-amber-500/50 shadow-lg hover:shadow-amber-500/25 transition-shadow duration-200">
				<button
					type="button"
					className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
					onClick={toggleAPIFunctionsSettings}
					aria-expanded={showAPIFunctionsSettings}
				>
					<h3 className="font-medium flex items-center">
						<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/40 via-orange-500/30 to-yellow-500/40 shadow-lg border border-amber-500/60 mr-3">
							<Wrench className="h-4 w-4 text-amber-300" />
						</div>
						<span className="text-textPrimary">Ferramentas de API</span>
						{functions.length > 0 && (
							<span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-300 rounded-full border border-amber-500/30">
								{functions.length} {functions.length === 1 ? 'função' : 'funções'}
							</span>
						)}
					</h3>
					<div
						className={`transform transition-transform duration-200 ${showAPIFunctionsSettings ? "rotate-180" : ""}`}
					>
						<ChevronDown
							className="h-5 w-5 text-textPrimary"
							aria-hidden="true"
						/>
					</div>
				</button>

				<div
					className={`transition-all duration-300 ease-in-out ${showAPIFunctionsSettings ? "max-h-[600px] opacity-100 overflow-y-auto" : "max-h-0 opacity-0 overflow-hidden"}`}
				>
					<div className="px-4 pb-4">
						<div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
							<div className="flex items-center justify-between text-amber-400 mb-2">
								<div className="flex items-center">
									<Info className="h-5 w-5 mr-2" />
									<span className="font-medium">Como funciona</span>
								</div>
								<button
									type="button"
									onClick={() => setIsHelpOpen(true)}
									className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-amber-500/20 text-amber-300 rounded-lg border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
								>
									<BookOpen className="h-3.5 w-3.5" />
									<span>Documentação</span>
								</button>
							</div>
							<p className="text-amber-200 text-sm">
								Cadastre APIs externas que a IA pode consultar durante conversas. Exemplos: consultar CEP, verificar estoque, buscar cotações.
							</p>
						</div>

						<div className={`flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-amber-500/25 mb-4 ${knowledgeBaseActive ? 'opacity-60' : ''}`}>
							<div className="flex items-center gap-3">
								<div>
									<label className="text-sm font-medium text-textPrimary">
										Habilitar Ferramentas de API
									</label>
									<p className="text-xs text-grayText">
										Permite que a IA consulte APIs externas cadastradas
									</p>
								</div>
							</div>
							<label className={`relative inline-flex items-center ${knowledgeBaseActive ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
								<input
									type="checkbox"
									name="API_FUNCTIONS_ENABLED"
									checked={knowledgeBaseActive ? false : isEnabled}
									onChange={onSettingsChange}
									disabled={knowledgeBaseActive}
									className="sr-only peer"
								/>
								<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-500/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600 peer-disabled:opacity-50"></div>
							</label>
						</div>

						{!isEnabled && !knowledgeBaseActive && (
							<div className="mb-4 flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
								<AlertTriangle className="w-4 h-4 flex-shrink-0" />
								<span>As funções cadastradas não serão utilizadas pela IA enquanto esta opção estiver desabilitada.</span>
							</div>
						)}

						{knowledgeBaseActive && (
							<div className="mb-4 flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
								<AlertTriangle className="w-4 h-4 flex-shrink-0" />
								<span>Ferramentas de API ficam desabilitadas enquanto a Base de Conhecimento está ativa (limitação da API).</span>
							</div>
						)}

						<button
							type="button"
							onClick={handleOpenDialog}
							className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 text-amber-300 font-medium transition-all hover:from-amber-500/30 hover:to-orange-500/30 hover:border-amber-400/60 hover:shadow-lg hover:shadow-amber-500/25 mb-4"
						>
							<Plus className="w-5 h-5" />
							<span>Gerenciar Funções de API</span>
						</button>

						{isLoading ? (
							<div className="text-center py-4 text-amber-300/70">
								Carregando funções...
							</div>
						) : functions.length === 0 ? (
							<div className="text-center py-6 text-amber-300/50 border border-dashed border-amber-500/30 rounded-lg">
								<Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
								<p className="text-sm">Nenhuma função cadastrada</p>
								<p className="text-xs mt-1">Clique em "Gerenciar Funções" para adicionar</p>
							</div>
						) : (
							<div className="space-y-2">
								{functions.slice(0, 5).map((func) => (
									<div
										key={func.id}
										className="flex items-center justify-between p-3 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-amber-500/20 hover:border-amber-500/40 transition-all"
									>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium text-textPrimary truncate">
													{func.name}
												</span>
												<span className="px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
													{func.method}
												</span>
											</div>
											<p className="text-xs text-grayText truncate mt-0.5">
												{func.description}
											</p>
										</div>
										<button
										type="button"
										onClick={() => handleDeleteFunction(func.id)}
										className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors ml-2"
										title="Remover função"
									>
										<Trash2 className="w-4 h-4" />
									</button>
									</div>
								))}
								{functions.length > 5 && (
									<p className="text-center text-xs text-amber-300/50 pt-2">
										+{functions.length - 5} mais funções...
									</p>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			<APIFunctionsDialog
				isOpen={isDialogOpen}
				onClose={handleCloseDialog}
				onReturnToMain={handleCloseDialog}
				instanceId={instanceId}
				onSave={() => loadFunctions()}
			/>

			<APIFunctionsHelpDialog
				isOpen={isHelpOpen}
				onClose={() => setIsHelpOpen(false)}
			/>
		</>
	);
};

export default APIFunctionsSection;
